import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import duration from 'dayjs/plugin/duration'
import patientIcon from 'features/Home/assets/patient.svg'
import type { FC } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useInterval, useToggle } from 'react-use'

import { Show } from 'common/components/Show/Show'
import {
  CHECK_FOR_DISABLED_CHANGE_ROOM_STATUS_INTERVAL,
  INFO_CONSTANTS,
  ROOM_STATUS,
  ROOM_STATUS_NAMES,
} from 'features/Home/constants/infoConstants'
import { InfoService } from 'features/Home/services/infoService'

import { Icon } from '@iconify/react'
import { AnnounceIconButton } from 'features/Home/Book/Booking/Schedule/Announce/announce'
import { AnnounceModal } from 'features/Home/Book/Booking/Schedule/Announce/announce-modal'
import { Link, useParams } from 'react-router'
import { PathUtils } from 'routes/routes.utils'
import styles from './documentResourceHeader.module.scss'
import type { DocumentResourceHeaderProps } from './documentResourceHeader.types'
import { DateService } from 'common/services/dateService'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(duration)
const { preserveDateInTimezone } = DateService

const getRoomStatusColor = (status: ROOM_STATUS) => {
  switch (status) {
    case ROOM_STATUS.BUSY:
      return { color: '#E32626', backgroundColor: '#FDF1F1' }
    case ROOM_STATUS.FREE:
      return { color: '#477648', backgroundColor: '#E4F2E4' }
    case ROOM_STATUS.CLEANUP:
      return { color: '#967A03', backgroundColor: '#FCF3CC' }
    case ROOM_STATUS.DISABLE:
      return { color: '#070707', backgroundColor: '#E7E7E7' }
    default:
      return { color: '#070707', backgroundColor: '#E7E7E7' }
  }
}

export const formatRoomName = (name: string): string => {
  const words = name.trim().split(/\s+/)

  const initials = words.map((word) => word.charAt(0).toUpperCase()).join('')

  const lastWord = words[words.length - 1]
  if (words.length > 1 && (/^[A-Za-z]$/.test(lastWord) || /^\d+$/.test(lastWord))) {
    return `${initials.slice(0, -1)} ${lastWord.toUpperCase()}`
  }

  return initials
}

const splitTime = (time: string) => {
  const [hours, minutes, seconds] = time.split(':')
  return { hours: parseInt(hours), minutes: parseInt(minutes), seconds: parseInt(seconds) }
}

export const DocumentResourceHeader: FC<DocumentResourceHeaderProps> = memo(
  ({
    room,
    hasRoomStatus = true,
    doctorsBufferTime,
    statusList = [],
    updateStatus = () => {},
    timeZone,
    patientsCount,
    disabledIntervals,
    dayInterval,
    hasOccupied,
  }) => {
    const { day, siteId } = useParams()
    const [openAnnounceModal, toggleAnnounceModal] = useToggle(false)
    const [dropdownDisabled, setDropDownDisabled] = useState(false)
    const [showStatusDropdown, setShowStatusDropdown] = useState(false)
    const statusButtonRef = useRef<HTMLDivElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })

    const getIsDisabledDropdown = () => {
      if (!hasOccupied) return true
      return InfoService.checkIfCurrentDateOverlapDisabledIntervals(
        disabledIntervals,
        room,
        dayInterval,
        timeZone,
        doctorsBufferTime,
      )
    }

    useInterval(() => {
      setDropDownDisabled(getIsDisabledDropdown())
    }, CHECK_FOR_DISABLED_CHANGE_ROOM_STATUS_INTERVAL)

    useEffect(() => {
      setDropDownDisabled(getIsDisabledDropdown())
    }, [room])

    // Handle click outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const isOutsideButton =
          statusButtonRef.current && !statusButtonRef.current.contains(event.target as Node)
        const isOutsideDropdown =
          dropdownRef.current && !dropdownRef.current.contains(event.target as Node)

        if (isOutsideButton && isOutsideDropdown) {
          setShowStatusDropdown(false)
        }
      }

      if (showStatusDropdown) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showStatusDropdown])

    const patientNumber = patientsCount >= 0 ? patientsCount : room.max_patients || 0

    const hasAnnounce = (() => {
      if (room.is_waiting_room || !room.add_patient) {
        // console.log('#1 condition', {
        //   is_waiting_room: room.is_waiting_room,
        //   add_patient: room.add_patient,
        //   room_id: room.id,
        //   room_name: room.label,
        // })
        return false
      }
      if (
        !preserveDateInTimezone(day, timeZone).isSame(
          preserveDateInTimezone(dayjs(), timeZone),
          'day',
        )
      ) {
        // console.log('#2 condition', {
        //   day: day,
        //   today: dayjs(),
        //   is_same_day: preserveDateInTimezone(day, timeZone).isSame(
        //     preserveDateInTimezone(dayjs(), timeZone),
        //     'day',
        //   ),
        //   room_id: room.id,
        //   room_name: room.label,
        // })
        return false
      }
      const todayStart = preserveDateInTimezone(dayjs(), timeZone).startOf('day')
      const currentTime = preserveDateInTimezone(dayjs(), timeZone)
      const hasOccupied = room.slots?.some((slot) => {
        const {
          hours: slotStartHours,
          minutes: slotStartMinutes,
          seconds: slotStartSeconds,
        } = splitTime(slot.start_time)
        const {
          hours: slotEndHours,
          minutes: slotEndMinutes,
          seconds: slotEndSeconds,
        } = splitTime(slot.end_time)
        const slotStart = todayStart
          .set('hour', slotStartHours)
          .set('minute', slotStartMinutes)
          .set('second', slotStartSeconds)
        const slotEnd = todayStart
          .set('hour', slotEndHours)
          .set('minute', slotEndMinutes)
          .set('second', slotEndSeconds)
        const isBetween = currentTime.isBetween(slotStart, slotEnd, null, '[]')
        // console.log('#3. condition', {
        //   slot_start: slotStart.format('HH:mm:ss'),
        //   slot_end: slotEnd.format('HH:mm:ss'),
        //   is_between_slots: isBetween,
        //   current_time: currentTime.format('HH:mm:ss'),
        //   room_id: room.id,
        //   room_name: room.label,
        // })
        return isBetween
      })

      return hasOccupied
    })()

    const link = useMemo(() => {
      if (!room.booking_id) return null
      return room?.is_promotion
        ? PathUtils.getPromotionDetails(siteId, room.booking_id)
        : PathUtils.getBookingDetails(siteId, room.booking_id)
    }, [room?.is_promotion, room.booking_id, siteId])

    const currentStatus = dropdownDisabled
      ? ROOM_STATUS.DISABLE
      : room?.status?.code || ROOM_STATUS.BUSY
    const statusColor = getRoomStatusColor(currentStatus)
    const statusName = ROOM_STATUS_NAMES[currentStatus] || 'Unknown'

    const handleStatusClick = useCallback(() => {
      if (!dropdownDisabled) {
        const buttonElement = statusButtonRef.current?.querySelector('button')
        if (buttonElement) {
          const rect = buttonElement.getBoundingClientRect()
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 4,
            left: rect.right - 120, // Align to right edge
            width: rect.width,
          })
        }
        setShowStatusDropdown(!showStatusDropdown)
      }
    }, [dropdownDisabled, showStatusDropdown])

    const handleStatusSelect = useCallback(
      (statusCode: number) => (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        updateStatus(statusCode, room)
        setShowStatusDropdown(false)
      },
      [updateStatus, room],
    )

    return (
      <>
        <AnnounceModal
          open={openAnnounceModal}
          toggleOpen={toggleAnnounceModal}
          rooms={[room.id]}
        />
        <div className={styles.parent}>
          <div className={styles.parentLeftSection}>
            {link && (
              <Link to={link}>
                <Icon
                  style={{ color: 'var(--tertiary-100)' }}
                  width={14}
                  height={14}
                  fontWeight={600}
                  icon='solar:link-bold'
                />
              </Link>
            )}
            <Show when={!!room?.add_patient}>
              <Tooltip
                title={
                  room.is_waiting_room
                    ? INFO_CONSTANTS.CURRENT_PATIENTS_IN_WAITING_ROOM
                    : INFO_CONSTANTS.SAME_TIME_ALLOWED_PATIENTS
                }
                placement='topLeft'>
                <div className={styles.parentPatientNumberContainer}>
                  <img
                    className={styles.parentPatientNumberIcon}
                    src={patientIcon}
                    alt='patient allowed slots'
                  />
                  <span className={styles.parentPatientNumber}>{patientNumber}</span>
                </div>
              </Tooltip>
            </Show>
          </div>

          <div className={styles.parentMiddleSection}>
            <h2 className={styles.parentRoomName}>
              <Tooltip title={room.label}>
                <span>{room.short_name || formatRoomName(room.label)}</span>
              </Tooltip>
            </h2>
            {hasAnnounce && (
              <AnnounceIconButton
                className={styles.announceIconButton}
                onClick={toggleAnnounceModal}
              />
            )}
          </div>

          <Show when={hasRoomStatus}>
            <div className={styles.parentRightSection}>
              <div className={styles.statusButtonContainer} ref={statusButtonRef}>
                <button
                  className={styles.statusButton}
                  onClick={handleStatusClick}
                  disabled={dropdownDisabled}
                  style={{
                    opacity: dropdownDisabled ? 0.5 : 1,
                    cursor: dropdownDisabled ? 'not-allowed' : 'pointer',
                  }}>
                  <div
                    className={styles.statusDot}
                    style={{ backgroundColor: statusColor.color }}
                  />
                  <span className={styles.statusLabel}>{statusName}</span>
                  <Icon
                    icon={showStatusDropdown ? 'ion:chevron-up' : 'ion:chevron-down'}
                    width={12}
                    height={12}
                    style={{ color: 'var(--dark-04)' }}
                  />
                </button>
              </div>
            </div>
          </Show>
        </div>
        {showStatusDropdown &&
          !dropdownDisabled &&
          createPortal(
            <div
              ref={dropdownRef}
              className={styles.statusDropdownPortal}
              style={{
                position: 'absolute',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 9999,
              }}>
              {statusList.map((status) => (
                <button
                  key={status.code}
                  className={styles.statusOption}
                  onClick={handleStatusSelect(status.code)}
                  style={{
                    backgroundColor:
                      status.code === currentStatus ? statusColor.backgroundColor : 'transparent',
                  }}>
                  <div
                    className={styles.statusDot}
                    style={{ backgroundColor: getRoomStatusColor(status.code).color }}
                  />
                  <span>{status.name}</span>
                </button>
              ))}
            </div>,
            document.body,
          )}
      </>
    )
  },
)
